import React, { createContext, useReducer, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFirebaseAuth } from './FirebaseAuthContext';
import { userService, lawyerService, appointmentService, chatService, subscriptionService } from '../services/firebaseService';
import { dashboardService } from '../services/dashboardService';
import dataSyncService from '../services/dataSyncService';
import toast from 'react-hot-toast';

// Initial state
const initialState = {
  // User state
  user: null,
  isAuthenticated: false,
  userRole: null, // 'client', 'lawyer', 'admin'
  isProfileComplete: false,
  
  // App settings
  theme: 'light',
  language: 'english',
  
  // App data
  lawyers: [],
  bookings: [],
  chats: [],
  aiChats: [],
  
  // UI state
  currentPage: 'welcome',
  isLoading: false,
  
  // Form data
  userProfile: null,
  lawyerProfile: null,
  
  // Subscription state
  subscription: null,
  hasActiveSubscription: false,
  
  // Wallet state
  walletBalance: 5000, // Default wallet balance
  
  // Recent activity
  recentActivity: [],
  
  // Dashboard metrics
  dashboardMetrics: {
    totalBookings: 0,
    pendingBookings: 0,
    completedBookings: 0,
    totalChats: 0,
    unreadMessages: 0,
    totalSpent: 0,
    lastActivity: null
  },
  
  // Plan restrictions
  planRestrictions: {
    canAccessLawyers: false,
    canBookConsultations: false,
    canUseVideoCalls: false,
    canUploadDocuments: false,
    canUseAIAssistant: false,
    canViewVerifiedLawyers: false,
    maxConsultations: 0,
    maxVideoCalls: 0,
    maxDocuments: 0
  },

  // Real-time data
  realtimeSubscriptions: []
};

// Action types
const ACTIONS = {
  SET_USER: 'SET_USER',
  SET_AUTHENTICATED: 'SET_AUTHENTICATED',
  SET_USER_ROLE: 'SET_USER_ROLE',
  SET_PROFILE_COMPLETE: 'SET_PROFILE_COMPLETE',
  SET_THEME: 'SET_THEME',
  SET_LANGUAGE: 'SET_LANGUAGE',
  SET_LAWYERS: 'SET_LAWYERS',
  SET_BOOKINGS: 'SET_BOOKINGS',
  SET_CHATS: 'SET_CHATS',
  SET_AI_CHATS: 'SET_AI_CHATS',
  SET_CURRENT_PAGE: 'SET_CURRENT_PAGE',
  SET_LOADING: 'SET_LOADING',
  SET_USER_PROFILE: 'SET_USER_PROFILE',
  SET_LAWYER_PROFILE: 'SET_LAWYER_PROFILE',
  ADD_BOOKING: 'ADD_BOOKING',
  ADD_CHAT: 'ADD_CHAT',
  ADD_AI_CHAT: 'ADD_AI_CHAT',
  UPDATE_BOOKING: 'UPDATE_BOOKING',
  SET_SUBSCRIPTION: 'SET_SUBSCRIPTION',
  UPDATE_WALLET_BALANCE: 'UPDATE_WALLET_BALANCE',
  LOGOUT: 'LOGOUT',
  ADD_ACTIVITY: 'ADD_ACTIVITY',
  UPDATE_PLAN_RESTRICTIONS: 'UPDATE_PLAN_RESTRICTIONS',
  ADD_REALTIME_SUBSCRIPTION: 'ADD_REALTIME_SUBSCRIPTION',
  CLEAR_REALTIME_SUBSCRIPTIONS: 'CLEAR_REALTIME_SUBSCRIPTIONS',
  UPDATE_DASHBOARD_METRICS: 'UPDATE_DASHBOARD_METRICS',
  SET_REALTIME_ACTIVITIES: 'SET_REALTIME_ACTIVITIES'
};

// Reducer function
const appReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_USER:
      return { ...state, user: action.payload };
    case ACTIONS.SET_AUTHENTICATED:
      return { ...state, isAuthenticated: action.payload };
    case ACTIONS.SET_USER_ROLE:
      return { ...state, userRole: action.payload };
    case ACTIONS.SET_PROFILE_COMPLETE:
      return { ...state, isProfileComplete: action.payload };
    case ACTIONS.SET_THEME:
      return { ...state, theme: action.payload };
    case ACTIONS.SET_LANGUAGE:
      return { ...state, language: action.payload };
    case ACTIONS.SET_LAWYERS:
      return { ...state, lawyers: action.payload };
    case ACTIONS.SET_BOOKINGS:
      return { ...state, bookings: action.payload };
    case ACTIONS.SET_CHATS:
      return { ...state, chats: action.payload };
    case ACTIONS.SET_AI_CHATS:
      return { ...state, aiChats: action.payload };
    case ACTIONS.SET_CURRENT_PAGE:
      return { ...state, currentPage: action.payload };
    case ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };
    case ACTIONS.SET_USER_PROFILE:
      return { ...state, userProfile: action.payload };
    case ACTIONS.SET_LAWYER_PROFILE:
      return { ...state, lawyerProfile: action.payload };
    case ACTIONS.ADD_BOOKING:
      return { ...state, bookings: [...state.bookings, action.payload] };
    case ACTIONS.ADD_CHAT:
      return { ...state, chats: [...state.chats, action.payload] };
    case ACTIONS.ADD_AI_CHAT:
      return { ...state, aiChats: [...state.aiChats, action.payload] };
    case ACTIONS.UPDATE_BOOKING:
      return {
        ...state,
        bookings: state.bookings.map(booking =>
          booking.id === action.payload.id ? action.payload : booking
        ),
      };
    case ACTIONS.SET_SUBSCRIPTION:
      return {
        ...state,
        subscription: action.payload,
        hasActiveSubscription: action.payload && action.payload.status === 'active',
      };
    case ACTIONS.UPDATE_WALLET_BALANCE:
      return {
        ...state,
        walletBalance: action.payload,
      };
    case ACTIONS.ADD_ACTIVITY:
      return {
        ...state,
        recentActivity: [action.payload, ...state.recentActivity.slice(0, 49)] // Keep last 50 activities
      };
    case ACTIONS.UPDATE_PLAN_RESTRICTIONS:
      return {
        ...state,
        planRestrictions: action.payload
      };
    case ACTIONS.ADD_REALTIME_SUBSCRIPTION:
      return {
        ...state,
        realtimeSubscriptions: [...state.realtimeSubscriptions, action.payload]
      };
    case ACTIONS.CLEAR_REALTIME_SUBSCRIPTIONS:
      return {
        ...state,
        realtimeSubscriptions: []
      };
    case ACTIONS.UPDATE_DASHBOARD_METRICS:
      return {
        ...state,
        dashboardMetrics: { ...state.dashboardMetrics, ...action.payload }
      };
    case ACTIONS.SET_REALTIME_ACTIVITIES:
      return {
        ...state,
        recentActivity: action.payload
      };
    case ACTIONS.LOGOUT:
      // Clear only non-essential localStorage data (keep theme, language, etc.)
      localStorage.removeItem('selectedRole');
      localStorage.removeItem('pendingLawyerProfile');
      
      // Clear all user-specific data using dataSyncService
      try {
        dataSyncService.clearLocalData();
      } catch (error) {
        console.error('Error clearing local data:', error);
      }
      
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        userRole: null,
        userProfile: null,
        lawyerProfile: null,
        subscription: null,
        hasActiveSubscription: false,
        currentPage: 'welcome',
        bookings: [],
        chats: [],
        aiChats: [],
        recentActivity: [],
        dashboardMetrics: initialState.dashboardMetrics,
        isProfileComplete: false,
        planRestrictions: initialState.planRestrictions,
        realtimeSubscriptions: []
      };
    default:
      return state;
  }
};

// Create context with default value
const AppContext = createContext(initialState);

// Provider component
const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const { user: currentUser, logout: firebaseLogout } = useFirebaseAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Load app preferences from localStorage on mount (non-auth data only)
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    const savedLanguage = localStorage.getItem('language') || 'english';
    const savedWalletBalance = localStorage.getItem('walletBalance');

    // Set saved values (only app preferences, not auth/profile data)
    dispatch({ type: ACTIONS.SET_THEME, payload: savedTheme });
    dispatch({ type: ACTIONS.SET_LANGUAGE, payload: savedLanguage });
    if (savedWalletBalance) {
      dispatch({ type: ACTIONS.UPDATE_WALLET_BALANCE, payload: parseFloat(savedWalletBalance) });
    }
  }, []);

  // Handle authentication state changes
  useEffect(() => {
    console.log('AppContext: Authentication state changed. Current user:', currentUser);
    
    if (currentUser && currentUser.uid) {
      console.log('User authenticated, loading data for:', currentUser.uid);
      dispatch({ type: ACTIONS.SET_USER, payload: currentUser });
      dispatch({ type: ACTIONS.SET_AUTHENTICATED, payload: true });
      
      // Store current user in localStorage for user-specific data
      localStorage.setItem('currentUser', JSON.stringify(currentUser));
      
      // Load user profile and role
      loadUserData(currentUser.uid);
    } else if (currentUser === null) {
      // Only clear state if currentUser is explicitly null (not undefined)
      console.log('No authenticated user, clearing state');
      dispatch({ type: ACTIONS.LOGOUT });
      
      // Clear current user from localStorage
      localStorage.removeItem('currentUser');
    } else {
      console.log('User state is undefined, waiting for authentication to resolve...');
      // Don't do anything - let the auth context handle it
    }
  }, [currentUser]);

  // Listen for chat updates from localStorage
  useEffect(() => {
    const handleChatUpdate = () => {
      const userChats = JSON.parse(localStorage.getItem('userChats') || '[]');
      console.log('🔍 Chat update detected, reloading chats:', userChats);
      dispatch({ type: ACTIONS.SET_CHATS, payload: userChats });
    };

    // Listen for custom chat update events
    window.addEventListener('chatUpdated', handleChatUpdate);
    
    // Also listen for storage changes (for cross-tab updates)
    const handleStorageChange = (e) => {
      if (e.key === 'userChats') {
        handleChatUpdate();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('chatUpdated', handleChatUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Handle navigation based on authentication state - REMOVED to prevent infinite loops
  // Navigation is now handled by route protection components

  // Load user data from Firestore
  const loadUserData = async (userId) => {
    if (!userId) {
      console.log('No user ID provided to loadUserData');
      return;
    }
    
    // Check if user is properly authenticated
    if (!currentUser) {
      console.log('No authenticated user, skipping data load');
      return;
    }
    
    // Basic validation for user ID
    if (!userId || typeof userId !== 'string') {
      console.log('Invalid user ID detected, skipping data load');
      return;
    }
    
    console.log('Loading user data for authenticated user:', userId);
    
    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });

      // Load user profile to determine role and completion status
      console.log('Loading user profile to check role and completion status...');
      const profileResult = await userService.getUserProfile(userId);
      
      if (profileResult.success && profileResult.data) {
        const profile = profileResult.data;
        console.log('User profile found:', profile);
        
        // Set user role from database (keep undefined if no role set)
        const userRole = profile.role || undefined;
        dispatch({ type: ACTIONS.SET_USER_ROLE, payload: userRole });
        
        if (userRole === 'lawyer') {
          console.log('🔍 User is a lawyer, checking for lawyer profile...');
          const lawyerResult = await lawyerService.getLawyerByUserId(userId);
          
          if (lawyerResult.success && lawyerResult.data) {
            console.log('✅ Lawyer profile found:', lawyerResult.data);
            // Set profile as complete for lawyers with profiles
            dispatch({ type: ACTIONS.SET_PROFILE_COMPLETE, payload: true });
            // Set lawyer profile data
            dispatch({ type: ACTIONS.SET_LAWYER_PROFILE, payload: lawyerResult.data });
          } else {
            console.log('❌ No lawyer profile found for user');
            dispatch({ type: ACTIONS.SET_PROFILE_COMPLETE, payload: false });
          }
          
          // Load user subscription for lawyers (they might have premium features)
          console.log('Loading lawyer subscription...');
          await loadUserSubscription(userId);
        } else if (userRole === 'client') {
          // For clients, use the profile_complete field from user profile
          console.log('👤 Client profile completion:', profile.profile_complete);
          dispatch({ type: ACTIONS.SET_PROFILE_COMPLETE, payload: profile.profile_complete || false });
          
          // Load user subscription for clients
          console.log('Loading client subscription...');
          await loadUserSubscription(userId);
        } else {
          // No role set yet - new user
          console.log('🆕 New user, no role set yet');
          
          // Load user subscription for new users
          console.log('Loading new user subscription...');
          await loadUserSubscription(userId);
        }
        
        // Set user profile data
        dispatch({ type: ACTIONS.SET_USER_PROFILE, payload: profile });
      } else {
        console.log('No user profile found, setting defaults');
        // Don't set any role or profile completion for new users
        // Let the AuthPage handle navigation to role selection
        dispatch({ type: ACTIONS.SET_USER_ROLE, payload: undefined });
        dispatch({ type: ACTIONS.SET_PROFILE_COMPLETE, payload: undefined });
      }
      
      // ===== COMPREHENSIVE CROSS-DEVICE SYNC =====
      const handleCrossDeviceSync = async () => {
        try {
          console.log('🔄 Starting cross-device sync for user:', userId);
          
          // Get user role from profile or localStorage
          const currentUserRole = profileResult.success && profileResult.data ? profileResult.data.role : 
                                 localStorage.getItem('userRole') || 'client';
          
          // 1. First, sync all local data to database (migration)
          const syncResult = await dataSyncService.syncAllLocalData(userId, currentUserRole);
          if (syncResult.success) {
            console.log('✅ Local data synced to database:', syncResult.results);
          }
          
          // 2. Then, load all data from database (cross-device)
          const loadResult = await dataSyncService.loadAllDataFromDatabase(userId, currentUserRole);
          if (loadResult.success) {
            console.log('✅ Data loaded from database:', loadResult.data);
            
            // Update context with loaded data
            if (loadResult.data.profile) {
              if (currentUserRole === 'lawyer') {
                dispatch({ type: ACTIONS.SET_LAWYER_PROFILE, payload: loadResult.data.profile });
              } else {
                dispatch({ type: ACTIONS.SET_USER_PROFILE, payload: loadResult.data.profile });
              }
            }
            
            if (loadResult.data.chats && loadResult.data.chats.length > 0) {
              dispatch({ type: ACTIONS.SET_CHATS, payload: loadResult.data.chats });
            }
            
            if (loadResult.data.wallet) {
              dispatch({ type: ACTIONS.UPDATE_WALLET_BALANCE, payload: loadResult.data.wallet.balance });
            }
          }
          
          // 3. Set up real-time sync subscription
          const unsubscribe = dataSyncService.subscribeToUserData(userId, (update) => {
            console.log('🔄 Real-time data update:', update);
            // Handle real-time updates here
          });
          
          // Store unsubscribe function for cleanup
          if (unsubscribe) {
            dispatch({ type: ACTIONS.ADD_REALTIME_SUBSCRIPTION, payload: { type: 'dataSync', unsubscribe } });
          }
          
        } catch (error) {
          console.error('❌ Cross-device sync error:', error);
        }
      };
      
      // Execute cross-device sync
      await handleCrossDeviceSync();
      
      // Load chats from localStorage (fallback)
      const userChats = JSON.parse(localStorage.getItem('userChats') || '[]');
      console.log('🔍 Loading chats from localStorage:', userChats);
      dispatch({ type: ACTIONS.SET_CHATS, payload: userChats });
      
      // Set empty arrays for bookings and aiChats to prevent errors
      dispatch({ type: ACTIONS.SET_BOOKINGS, payload: [] });
      dispatch({ type: ACTIONS.SET_AI_CHATS, payload: [] });
      
      // Load dashboard metrics and recent activities
      const userRole = profileResult.success && profileResult.data ? profileResult.data.role : 'client';
      await loadDashboardData(userId, userRole);
      
      // Load recent activities from localStorage only (no sample data)
      const savedActivities = JSON.parse(localStorage.getItem('recentActivity') || '[]');
        dispatch({ type: ACTIONS.SET_REALTIME_ACTIVITIES, payload: savedActivities || [] });
      
      console.log('Essential user data loaded successfully');

    } catch (error) {
      console.error('Error loading user data:', error);
      toast.error('Failed to load user data');
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Load dashboard data
  const loadDashboardData = async (userId, userRole = 'client') => {
    try {
      console.log('🔍 Loading dashboard data for user:', userId, 'role:', userRole);
      
      // Calculate dashboard metrics
      const metricsResult = await dashboardService.calculateDashboardMetrics(userId, userRole);
      if (metricsResult.success) {
        dispatch({ 
          type: ACTIONS.UPDATE_DASHBOARD_METRICS, 
          payload: metricsResult.data
        });
      } else {
        // Use empty metrics if calculation fails (no sample data)
        dispatch({ 
          type: ACTIONS.UPDATE_DASHBOARD_METRICS, 
          payload: {
            totalBookings: 0,
            completedBookings: 0,
            pendingBookings: 0,
            totalEarnings: 0,
            monthlyEarnings: 0,
            averageRating: 0,
            totalReviews: 0
          }
        });
      }
      
      // Get recent activities
      const activitiesResult = await dashboardService.getRecentActivities(userId);
      if (activitiesResult.success) {
        dispatch({ type: ACTIONS.SET_REALTIME_ACTIVITIES, payload: activitiesResult.data });
      }
      
    } catch (error) {
      console.error('❌ Error loading dashboard data:', error);
      
      // Use empty data as fallback (no sample data)
      dispatch({ 
        type: ACTIONS.UPDATE_DASHBOARD_METRICS, 
        payload: {
          totalBookings: 0,
          completedBookings: 0,
          pendingBookings: 0,
          totalEarnings: 0,
          monthlyEarnings: 0,
          averageRating: 0,
          totalReviews: 0
        }
      });
      dispatch({ type: ACTIONS.SET_REALTIME_ACTIVITIES, payload: [] });
    }
  };

  // Set up real-time subscriptions
  const setupRealtimeSubscriptions = (userId) => {
    // Clear existing subscriptions
    state.realtimeSubscriptions.forEach(sub => {
      if (sub && typeof sub.unsubscribe === 'function') {
        sub.unsubscribe();
      }
    });
    dispatch({ type: ACTIONS.CLEAR_REALTIME_SUBSCRIPTIONS });

    // Firebase real-time subscriptions will be handled by individual components
    // as they provide better granular control and performance
    console.log('Firebase real-time subscriptions will be set up by individual components');
  };

  // Context functions
  const setUser = (user) => {
    dispatch({ type: ACTIONS.SET_USER, payload: user });
  };

  const setAuthenticated = (isAuthenticated) => {
    dispatch({ type: ACTIONS.SET_AUTHENTICATED, payload: isAuthenticated });
  };

  const setUserRole = (role) => {
    dispatch({ type: ACTIONS.SET_USER_ROLE, payload: role });
    localStorage.setItem('userRole', role);
  };

  const setProfileComplete = (isComplete) => {
    dispatch({ type: ACTIONS.SET_PROFILE_COMPLETE, payload: isComplete });
    localStorage.setItem('isProfileComplete', JSON.stringify(isComplete));
  };

  const setTheme = (theme) => {
    dispatch({ type: ACTIONS.SET_THEME, payload: theme });
    localStorage.setItem('theme', theme);
  };

  const setLanguage = (language) => {
    dispatch({ type: ACTIONS.SET_LANGUAGE, payload: language });
    localStorage.setItem('language', language);
  };

  const setLawyers = (lawyers) => {
    dispatch({ type: ACTIONS.SET_LAWYERS, payload: lawyers });
  };

  const setBookings = (bookings) => {
    dispatch({ type: ACTIONS.SET_BOOKINGS, payload: bookings });
  };

  const setChats = (chats) => {
    dispatch({ type: ACTIONS.SET_CHATS, payload: chats });
  };

  const setAiChats = (aiChats) => {
    dispatch({ type: ACTIONS.SET_AI_CHATS, payload: aiChats });
  };

  const setCurrentPage = (page) => {
    dispatch({ type: ACTIONS.SET_CURRENT_PAGE, payload: page });
    localStorage.setItem('currentPage', page);
  };

  const setLoading = (loading) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
  };

  const setUserProfile = (profile) => {
    dispatch({ type: ACTIONS.SET_USER_PROFILE, payload: profile });
    localStorage.setItem('userProfile', JSON.stringify(profile));
  };

  const setLawyerProfile = (profile) => {
    dispatch({ type: ACTIONS.SET_LAWYER_PROFILE, payload: profile });
    localStorage.setItem('lawyerProfile', JSON.stringify(profile));
  };

  const addBooking = (booking) => {
    dispatch({ type: ACTIONS.ADD_BOOKING, payload: booking });
  };

  const addChat = (chat) => {
    dispatch({ type: ACTIONS.ADD_CHAT, payload: chat });
  };

  const addAiChat = (aiChat) => {
    dispatch({ type: ACTIONS.ADD_AI_CHAT, payload: aiChat });
  };

  const updateBooking = (booking) => {
    dispatch({ type: ACTIONS.UPDATE_BOOKING, payload: booking });
  };

  const setSubscription = (subscription) => {
    dispatch({ type: ACTIONS.SET_SUBSCRIPTION, payload: subscription });
    localStorage.setItem('subscription', JSON.stringify(subscription));
  };

  const updateWalletBalance = (balance) => {
    dispatch({ type: ACTIONS.UPDATE_WALLET_BALANCE, payload: balance });
    localStorage.setItem('walletBalance', balance.toString());
  };

  const addActivity = useCallback(async (activity) => {
    try {
      const userId = state.user?.uid || state.user?.id;
      if (!userId) {
        console.warn('⚠️ No user ID available for activity logging');
        return;
      }
      
      const result = await dashboardService.addActivity(userId, activity);
      if (result.success) {
        dispatch({ type: ACTIONS.ADD_ACTIVITY, payload: result.data });
      }
    } catch (error) {
      console.error('❌ Error adding activity:', error);
    }
  }, [state.user]);

  const updateDashboardMetrics = (metrics) => {
    dispatch({ type: ACTIONS.UPDATE_DASHBOARD_METRICS, payload: metrics });
  };

  const setRealtimeActivities = (activities) => {
    dispatch({ type: ACTIONS.SET_REALTIME_ACTIVITIES, payload: activities });
  };

  const updatePlanRestrictions = (restrictions) => {
    dispatch({ type: ACTIONS.UPDATE_PLAN_RESTRICTIONS, payload: restrictions });
  };

  // Load user subscription
  const loadUserSubscription = async (userId) => {
    if (!userId) return;
    
    try {
      const result = await subscriptionService.getCurrentUserSubscription();
      if (result.success && result.data) {
        setSubscription(result.data);
        
        // Update plan restrictions based on subscription
        const restrictions = {
          canAccessLawyers: true, // All plans can access lawyers
          canBookConsultations: result.data.consultationsRemaining > 0, // Can book if consultations remaining
          canUseVideoCalls: result.data.planType !== 'free', // Only paid plans can use video calls
          canUploadDocuments: result.data.planType !== 'free', // Only paid plans can upload documents
          canUseAIAssistant: true, // All plans can use AI assistant
          canViewVerifiedLawyers: true, // All authenticated users can view lawyer profiles
          maxConsultations: result.data.consultationsRemaining,
          maxVideoCalls: result.data.planType !== 'free' ? result.data.consultationsRemaining : 0,
          maxDocuments: result.data.planType !== 'free' ? 999 : 0 // Unlimited for paid plans
        };
        updatePlanRestrictions(restrictions);
      } else {
        // No subscription found, create free trial
        const trialResult = await subscriptionService.createFreeTrial();
        if (trialResult.success) {
          // Reload subscription after creating trial
          const reloadResult = await subscriptionService.getCurrentUserSubscription();
          if (reloadResult.success && reloadResult.data) {
            setSubscription(reloadResult.data);
            toast.success('Free trial activated!');
          }
        } else {
          // Set default restrictions for users without subscription
          const defaultRestrictions = {
            canAccessLawyers: true,
            canBookConsultations: false,
            canUseVideoCalls: false,
            canUploadDocuments: false,
            canUseAIAssistant: true,
            canViewVerifiedLawyers: true,
            maxConsultations: 0,
            maxVideoCalls: 0,
            maxDocuments: 0
          };
          updatePlanRestrictions(defaultRestrictions);
        }
      }
    } catch (error) {
      console.error('Error loading user subscription:', error);
    }
  };

  // Check if user can use a feature
  const checkFeatureAccess = async (featureType) => {
    try {
      const result = await subscriptionService.canUseFeature(featureType);
      return result.success && result.data;
    } catch (error) {
      console.error('Error checking feature access:', error);
      return false;
    }
  };

  // Record feature usage
  const recordFeatureUsage = async (featureType) => {
    try {
      const result = await subscriptionService.recordFeatureUsage(featureType);
      if (result.success) {
        // Reload subscription to get updated usage
        await loadUserSubscription(state.user?.id);
        return true;
      } else {
        toast.error('Feature usage limit reached. Please upgrade your plan.');
        return false;
      }
    } catch (error) {
      console.error('Error recording feature usage:', error);
      return false;
    }
  };

  // Upgrade subscription
  const upgradeSubscription = async (planId, paymentMethod, amount) => {
    try {
      const result = await subscriptionService.upgradeSubscription(planId, paymentMethod, amount);
      if (result.success) {
        // Reload subscription
        await loadUserSubscription(state.user?.id);
        toast.success('Subscription upgraded successfully!');
        return true;
      } else {
        toast.error('Failed to upgrade subscription');
        return false;
      }
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      toast.error('Failed to upgrade subscription');
      return false;
    }
  };

  // Cancel subscription
  const cancelSubscription = async (reason = null) => {
    try {
      const result = await subscriptionService.cancelSubscription(reason);
      if (result.success) {
        // Reload subscription
        await loadUserSubscription(state.user?.id);
        toast.success('Subscription cancelled successfully');
        return true;
      } else {
        toast.error('Failed to cancel subscription');
        return false;
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Failed to cancel subscription');
      return false;
    }
  };

  const logout = async () => {
    try {
      // Clear real-time subscriptions
      state.realtimeSubscriptions.forEach(sub => {
        if (sub && typeof sub.unsubscribe === 'function') {
          sub.unsubscribe();
        }
      });

      // Logout from Firebase
      await firebaseLogout();
      
      // Clear app state
      dispatch({ type: ACTIONS.LOGOUT });
      
      // Clear role from localStorage
      localStorage.removeItem('selectedRole');
      
      // Clear current user from localStorage
      localStorage.removeItem('currentUser');
      
      // Navigate to auth page
      navigate('/auth');
      
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed');
    }
  };

  const value = {
    // State
    ...state,
    
    // Actions
    setUser,
    setAuthenticated,
    setUserRole,
    setProfileComplete,
    setTheme,
    setLanguage,
    setLawyers,
    setBookings,
    setChats,
    setAiChats,
    setCurrentPage,
    setLoading,
    setUserProfile,
    setLawyerProfile,
    addBooking,
    addChat,
    addAiChat,
    updateBooking,
    setSubscription,
    updateWalletBalance,
    addActivity,
    updateDashboardMetrics,
    setRealtimeActivities,
    updatePlanRestrictions,
    loadUserSubscription,
    checkFeatureAccess,
    recordFeatureUsage,
    upgradeSubscription,
    cancelSubscription,
    logout,
    loadUserData,
    loadDashboardData
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export { AppContext, AppProvider };